import { useState, useEffect, useCallback, useMemo } from 'react';
import { flujoCajaApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { Switch } from '../ui/switch';
import { toast } from '../../hooks/use-toast';
import { FechaPagoPicker } from './FechaPagoPicker';
import { OtrosIngresosFactoringSection } from './factoring/OtrosIngresosFactoringSection';
import {
  Plus, Pencil, Trash2, CheckCircle2, Unlock, AlertTriangle, Landmark,
  FileText, Search,
} from 'lucide-react';

const formatCLP = (v) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(v || 0);
const formatCLPShort = (v) => new Intl.NumberFormat('es-CL', { maximumFractionDigits: 0 }).format(v || 0);
const todayISO = () => new Date().toISOString().split('T')[0];

/**
 * Wrapper específico de FactoringPanel que conecta el FechaPagoPicker reusable
 * con el endpoint de factoring asignaciones (`setFechaPagoFactoring`).
 */
function AsigFechaPagoPicker({ asig, onChange }) {
  const handleSet = async (iso) => {
    try {
      await flujoCajaApi.setFechaPagoFactoring(asig.id, iso);
      toast({ title: `Fecha pago efectiva: ${iso}` });
      if (onChange) onChange();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };
  const handleClear = async () => {
    try {
      await flujoCajaApi.setFechaPagoFactoring(asig.id, null);
      toast({ title: 'Vuelve a usar la fecha de Alegra' });
      if (onChange) onChange();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };
  return (
    <FechaPagoPicker
      fechaOriginal={asig.fecha_venc || ''}
      fechaModificada={asig.fecha_pago_modificada || ''}
      vencida={!!asig.vencida}
      onSet={handleSet}
      onClear={handleClear}
      labelOriginal="Alegra"
      labelModificada="Flujo"
      testId={`fecha-pago-${asig.id}`}
    />
  );
}

const emptyLinea = () => ({
  nombre: '', banco: '', cupo_total: 0,
  orden_fila_asignado: '', orden_fila_disponible: '',
  tasa_mensual_pct: 0, activa: true, color: '#3b82f6', nota: '',
});

export default function FactoringPanel() {
  const [loading, setLoading] = useState(true);
  const [lineas, setLineas] = useState([]);
  const [resumen, setResumen] = useState({ total_cupo: 0, total_asignado: 0, total_vencido: 0, total_disponible: 0 });
  const [asignacionesPorLinea, setAsignacionesPorLinea] = useState({});
  const [editingLinea, setEditingLinea] = useState(null);
  const [formLinea, setFormLinea] = useState(emptyLinea());
  const [savingLinea, setSavingLinea] = useState(false);
  const [asignarDialog, setAsignarDialog] = useState(null); // linea
  const [vencidasDialog, setVencidasDialog] = useState(null); // linea (o 'all')
  const [cxcDisponibles, setCxcDisponibles] = useState([]);
  const [cxcDispLoading, setCxcDispLoading] = useState(true);
  // Iter 270 (Carlos): discrepancias (CxC pagada/cobrada pero factoring aún 'asignada')
  const [discrepancias, setDiscrepancias] = useState([]);
  const [recuperando, setRecuperando] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setCxcDispLoading(true);
    try {
      const [resLineas, resAsig, resDisp] = await Promise.all([
        flujoCajaApi.listFactoringLineas(true),
        flujoCajaApi.listFactoringAsignaciones({ estado: 'asignada' }),
        flujoCajaApi.cxcDisponiblesFactoring(),
      ]);
      setLineas(resLineas.data.items || []);
      setResumen(resLineas.data.resumen || {});
      // agrupar asignaciones por linea_id
      const grouped = {};
      (resAsig.data.items || []).forEach((a) => {
        if (!grouped[a.linea_id]) grouped[a.linea_id] = [];
        grouped[a.linea_id].push(a);
      });
      setAsignacionesPorLinea(grouped);
      setCxcDisponibles((resDisp.data.items || []).filter((c) => c.disponible_para_factorizar > 0));
      try {
        const resDisc = await flujoCajaApi.factoringDiscrepancias();
        setDiscrepancias(resDisc.data.items || []);
      } catch { /* silent */ }
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al cargar factoring', variant: 'destructive' });
    } finally {
      setLoading(false);
      setCxcDispLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRecuperar = async (item) => {
    setRecuperando(item.asig_id);
    try {
      const res = await flujoCajaApi.marcarRecuperadaAsignacion(item.asig_id);
      toast({ title: res?.data?.message || `Recuperación registrada · folio ${item.folio}` });
      await load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al marcar recuperada', variant: 'destructive' });
    } finally {
      setRecuperando(null);
    }
  };

  const openNuevaLinea = () => { setFormLinea(emptyLinea()); setEditingLinea('new'); };
  const openEditLinea = (l) => {
    setFormLinea({
      nombre: l.nombre, banco: l.banco || '', cupo_total: l.cupo_total,
      orden_fila_asignado: l.orden_fila_asignado || '',
      orden_fila_disponible: l.orden_fila_disponible || '',
      tasa_mensual_pct: l.tasa_mensual_pct || 0,
      activa: l.activa, color: l.color || '#3b82f6', nota: l.nota || '',
    });
    setEditingLinea(l.id);
  };

  const saveLinea = async () => {
    if (!formLinea.nombre.trim()) return toast({ title: 'Nombre requerido', variant: 'destructive' });
    setSavingLinea(true);
    try {
      const payload = {
        ...formLinea,
        cupo_total: Number(formLinea.cupo_total) || 0,
        orden_fila_asignado: formLinea.orden_fila_asignado ? Number(formLinea.orden_fila_asignado) : null,
        orden_fila_disponible: formLinea.orden_fila_disponible ? Number(formLinea.orden_fila_disponible) : null,
        tasa_mensual_pct: Number(formLinea.tasa_mensual_pct) || 0,
      };
      if (editingLinea === 'new') await flujoCajaApi.createFactoringLinea(payload);
      else await flujoCajaApi.updateFactoringLinea(editingLinea, payload);
      toast({ title: 'Línea guardada' });
      setEditingLinea(null);
      await load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al guardar', variant: 'destructive' });
    } finally {
      setSavingLinea(false);
    }
  };

  const deleteLinea = async (id) => {
    if (!window.confirm('¿Eliminar esta línea de factoring?')) return;
    try {
      await flujoCajaApi.deleteFactoringLinea(id);
      toast({ title: 'Línea eliminada' });
      await load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al eliminar', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="space-y-3" data-testid="factoring-loading">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="factoring-panel">
      {/* Resumen global */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Card className="border-slate-200">
          <CardContent className="p-3">
            <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Cupo Total</div>
            <div className="text-lg font-bold tabular-nums text-slate-900 dark:text-slate-100" data-testid="sum-cupo">{formatCLP(resumen.total_cupo)}</div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50/40">
          <CardContent className="p-3">
            <div className="text-[11px] text-blue-700 uppercase tracking-wide">Asignado</div>
            <div className="text-lg font-bold tabular-nums text-blue-900" data-testid="sum-asignado">{formatCLP(resumen.total_asignado)}</div>
          </CardContent>
        </Card>
        <Card
          className="border-red-200 bg-red-50/40 cursor-pointer hover:bg-red-100/60 transition"
          onClick={() => resumen.total_vencido > 0 && setVencidasDialog('all')}
          data-testid="card-vencidos-global"
          title="Clic para ver facturas vencidas"
        >
          <CardContent className="p-3">
            <div className="text-[11px] text-red-700 uppercase tracking-wide flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Vencido
            </div>
            <div className="text-lg font-bold tabular-nums text-red-700" data-testid="sum-vencido">{formatCLP(resumen.total_vencido)}</div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50/40">
          <CardContent className="p-3">
            <div className="text-[11px] text-emerald-700 uppercase tracking-wide">Disponible</div>
            <div className="text-lg font-bold tabular-nums text-emerald-700" data-testid="sum-disponible">{formatCLP(resumen.total_disponible)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Iter 270: Discrepancias — pagadas en sistema pero aún asignadas en factoring */}
      {discrepancias.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/60" data-testid="factoring-discrepancias">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-amber-800 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Facturas pagadas pendientes de recuperar en factoring ({discrepancias.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0">
            <p className="text-[12px] text-amber-700 mb-3">
              Estas facturas figuran <b>pagadas/cobradas</b> en el sistema pero su asignación de factoring
              sigue en <b>"asignada"</b> (el cupo no se ha liberado). Marca la <b>recuperación</b> para liberar
              el cupo. El pago ya fue registrado vía la CxC, por lo que <b>NO se vuelve a registrar en Alegra</b>.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-amber-900 border-b border-amber-200">
                    <th className="px-2 py-1.5">Folio</th>
                    <th className="px-2 py-1.5">Cliente</th>
                    <th className="px-2 py-1.5">Línea</th>
                    <th className="px-2 py-1.5">Estado CxC</th>
                    <th className="px-2 py-1.5">Fecha pago</th>
                    <th className="px-2 py-1.5 text-right">Monto factorizado</th>
                    <th className="px-2 py-1.5 text-right">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {discrepancias.map((d) => (
                    <tr key={d.asig_id} className="border-b border-amber-100 hover:bg-amber-100/40" data-testid={`discrepancia-row-${d.folio}`}>
                      <td className="px-2 py-1.5 font-mono font-bold text-amber-900">{d.folio}</td>
                      <td className="px-2 py-1.5 truncate max-w-[180px]" title={d.cliente}>{d.cliente}</td>
                      <td className="px-2 py-1.5">{d.linea_nombre}</td>
                      <td className="px-2 py-1.5">
                        <Badge variant="outline" className="text-[10px] capitalize">{d.cxc_estado}</Badge>
                      </td>
                      <td className="px-2 py-1.5 text-muted-foreground">{d.fecha_pago || '—'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {formatCLP(d.monto_asignado)}
                        {!d.montos_calzan && (
                          <span className="ml-1 text-[9px] font-semibold bg-red-100 text-red-700 border border-red-300 rounded px-1"
                            title={`El monto factorizado (${formatCLP(d.monto_asignado)}) no calza con el monto de la CxC (${formatCLP(d.monto_cxc)}). Revisar manualmente.`}
                            data-testid={`discrepancia-descalce-${d.folio}`}>≠ CxC</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <Button size="sm" variant="outline"
                          className="h-7 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          disabled={recuperando === d.asig_id}
                          onClick={() => handleRecuperar(d)}
                          data-testid={`btn-recuperar-${d.folio}`}>
                          <Unlock className="w-3 h-3 mr-1" />
                          {recuperando === d.asig_id ? 'Procesando…' : 'Marcar recuperada'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Botón nueva línea */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm text-muted-foreground">
          {lineas.length} línea{lineas.length !== 1 ? 's' : ''} registrada{lineas.length !== 1 ? 's' : ''}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={openNuevaLinea} data-testid="btn-nueva-linea">
            <Plus className="w-4 h-4 mr-1" /> Nueva línea
          </Button>
        </div>
      </div>

      {/* Cards por línea */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {lineas.map((l) => (
          <LineaCard
            key={l.id}
            linea={l}
            asignaciones={asignacionesPorLinea[l.id] || []}
            onEdit={() => openEditLinea(l)}
            onDelete={() => deleteLinea(l.id)}
            onAsignar={() => setAsignarDialog(l)}
            onVerVencidas={() => setVencidasDialog(l)}
            onRefresh={load}
          />
        ))}
        {lineas.length === 0 && (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center text-muted-foreground">
              <Landmark className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No hay líneas de factoring. Crea la primera con el botón "Nueva línea".</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Sección: CxC disponibles para factorizar (no asociadas aún) */}
      <CxcDisponiblesSection
        loading={cxcDispLoading}
        items={cxcDisponibles}
        lineas={lineas.filter((l) => l.activa)}
        onAsignar={(linea) => setAsignarDialog(linea)}
        onRefresh={load}
      />

      {/* Iter 236.o (Carlos): Otros Ingresos factorizables, debajo de CxC */}
      <OtrosIngresosFactoringSection
        lineas={lineas.filter((l) => l.activa)}
        onAsignacionChange={load}
      />

      {/* Dialog CRUD Línea */}
      <Dialog open={!!editingLinea} onOpenChange={(o) => !o && setEditingLinea(null)}>
        <DialogContent className="max-w-xl" data-testid="dialog-linea">
          <DialogHeader>
            <DialogTitle>{editingLinea === 'new' ? 'Nueva línea de factoring' : 'Editar línea'}</DialogTitle>
            <DialogDescription>Define el cupo y opcionalmente conéctala a filas del grid de flujo.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Nombre *</Label>
              <Input value={formLinea.nombre} onChange={(e) => setFormLinea({ ...formLinea, nombre: e.target.value })} data-testid="input-linea-nombre" />
            </div>
            <div>
              <Label>Banco / Entidad</Label>
              <Input value={formLinea.banco} onChange={(e) => setFormLinea({ ...formLinea, banco: e.target.value })} data-testid="input-linea-banco" />
            </div>
            <div>
              <Label>Cupo total (CLP)</Label>
              <Input type="number" value={formLinea.cupo_total} onChange={(e) => setFormLinea({ ...formLinea, cupo_total: e.target.value })} data-testid="input-linea-cupo" />
            </div>
            <div>
              <Label>Fila "asignado" en grid</Label>
              <Input type="number" placeholder="ej. 50" value={formLinea.orden_fila_asignado} onChange={(e) => setFormLinea({ ...formLinea, orden_fila_asignado: e.target.value })} data-testid="input-linea-orden-asig" />
            </div>
            <div>
              <Label>Fila "disponible" en grid</Label>
              <Input type="number" placeholder="ej. 51" value={formLinea.orden_fila_disponible} onChange={(e) => setFormLinea({ ...formLinea, orden_fila_disponible: e.target.value })} data-testid="input-linea-orden-disp" />
            </div>
            <div>
              <Label>Tasa mensual (%)</Label>
              <Input type="number" step="0.01" value={formLinea.tasa_mensual_pct} onChange={(e) => setFormLinea({ ...formLinea, tasa_mensual_pct: e.target.value })} />
            </div>
            <div>
              <Label>Color</Label>
              <Input type="color" value={formLinea.color} onChange={(e) => setFormLinea({ ...formLinea, color: e.target.value })} />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <Switch checked={formLinea.activa} onCheckedChange={(v) => setFormLinea({ ...formLinea, activa: v })} data-testid="switch-linea-activa" />
              <Label>Línea activa</Label>
            </div>
            <div className="col-span-2">
              <Label>Nota</Label>
              <Input value={formLinea.nota} onChange={(e) => setFormLinea({ ...formLinea, nota: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingLinea(null)}>Cancelar</Button>
            <Button onClick={saveLinea} disabled={savingLinea} data-testid="btn-save-linea">
              {savingLinea ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Asignar factura */}
      {asignarDialog && (
        <AsignarFacturaDialog
          linea={asignarDialog}
          onClose={() => setAsignarDialog(null)}
          onDone={() => { setAsignarDialog(null); load(); }}
        />
      )}

      {/* Dialog Vencidas */}
      {vencidasDialog && (
        <VencidasDialog
          scope={vencidasDialog}
          onClose={() => setVencidasDialog(null)}
        />
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════
   Card por línea: estadísticas + lista de asignaciones + acciones
   ═══════════════════════════════════════════════════════════════════════ */
function LineaCard({ linea, asignaciones, onEdit, onDelete, onAsignar, onVerVencidas, onRefresh }) {
  const [confirmPay, setConfirmPay] = useState(null);  // { asig }
  const [paying, setPaying] = useState(false);

  const marcarPagada = (asig) => setConfirmPay({ asig });

  const doMarcarPagada = async () => {
    if (!confirmPay?.asig) return;
    const asig = confirmPay.asig;
    setPaying(true);
    try {
      const res = await flujoCajaApi.marcarPagadaAsignacion(asig.id);
      const alegra = res.data?.alegra;
      if (alegra?.synced) {
        toast({
          title: 'Pagada · sincronizada con Alegra',
          description: `Pago #${alegra.payment_id} en ${alegra.account} (${alegra.method})`,
        });
      } else {
        toast({
          title: 'Pagada localmente · pendiente sync Alegra',
          description: alegra?.error || 'Reintenta desde la UI',
          variant: 'destructive',
        });
      }
      setConfirmPay(null);
      await onRefresh();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally {
      setPaying(false);
    }
  };

  // Reservada para uso futuro: botón de "Reintentar push a Alegra" por asignación.
  // Se mantiene aquí porque el endpoint backend ya existe.
  const _reintentarAlegra = async (asig) => {
    try {
      await flujoCajaApi.reintentarAlegraAsignacion(asig.id);
      toast({ title: 'Pago sincronizado con Alegra' });
      await onRefresh();
    } catch (err) {
      toast({ title: 'No se pudo sincronizar', description: err?.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };

  const liberar = async (asig) => {
    if (!window.confirm(`¿Liberar la asignación ${asig.folio} por ${formatCLPShort(asig.monto_asignado)}?\nDevuelve el cupo sin marcar la CxC como pagada.`)) return;
    try {
      await flujoCajaApi.liberarAsignacion(asig.id);
      toast({ title: 'Asignación liberada' });
      await onRefresh();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };

  const vencidas = asignaciones.filter((a) => a.vencida);
  const pctUso = linea.cupo_total > 0 ? Math.min(100, (linea.total_asignado / linea.cupo_total) * 100) : 0;

  return (
    <Card
      className={`border-2 ${!linea.activa ? 'opacity-60' : ''}`}
      style={{ borderLeftColor: linea.color, borderLeftWidth: 6 }}
      data-testid={`linea-card-${linea.id}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="text-base flex items-center gap-2">
              <Landmark className="w-4 h-4" style={{ color: linea.color }} />
              {linea.nombre}
              {!linea.activa && <Badge variant="outline" className="text-[10px]">inactiva</Badge>}
            </CardTitle>
            <div className="text-xs text-muted-foreground mt-0.5">
              {linea.banco || 'Sin banco'}
              {linea.orden_fila_asignado && ` · Grid fila ${linea.orden_fila_asignado}/${linea.orden_fila_disponible || '?'}`}
            </div>
          </div>
          <div className="flex gap-1 flex-shrink-0">
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onEdit} data-testid={`btn-edit-linea-${linea.id}`}>
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={onDelete} data-testid={`btn-delete-linea-${linea.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {/* Estadísticas */}
        <div className="grid grid-cols-4 gap-2 text-center">
          <div className="bg-slate-50 dark:bg-slate-900/30 rounded p-2">
            <div className="text-[10px] text-muted-foreground uppercase">Cupo</div>
            <div className="text-sm font-bold tabular-nums">{formatCLPShort(linea.cupo_total)}</div>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded p-2">
            <div className="text-[10px] text-blue-700 uppercase">Asignado</div>
            <div className="text-sm font-bold tabular-nums text-blue-900">{formatCLPShort(linea.total_asignado)}</div>
          </div>
          <button
            type="button"
            className={`rounded p-2 transition ${vencidas.length > 0 ? 'bg-red-50 hover:bg-red-100 cursor-pointer' : 'bg-slate-50 cursor-default'}`}
            onClick={() => vencidas.length > 0 && onVerVencidas()}
            disabled={vencidas.length === 0}
            data-testid={`btn-ver-vencidas-${linea.id}`}
          >
            <div className="text-[10px] uppercase flex items-center gap-0.5 justify-center" style={{ color: vencidas.length > 0 ? '#b91c1c' : '#64748b' }}>
              {vencidas.length > 0 && <AlertTriangle className="w-3 h-3" />} Vencido
            </div>
            <div className={`text-sm font-bold tabular-nums ${vencidas.length > 0 ? 'text-red-700' : 'text-slate-500'}`}>
              {formatCLPShort(linea.total_vencido)}
            </div>
          </button>
          <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded p-2">
            <div className="text-[10px] text-emerald-700 uppercase">Disp.</div>
            <div className="text-sm font-bold tabular-nums text-emerald-700">{formatCLPShort(linea.disponible)}</div>
          </div>
        </div>

        {/* Barra de uso */}
        <div>
          <div className="flex justify-between text-[10px] text-muted-foreground mb-0.5">
            <span>Uso del cupo</span>
            <span>{pctUso.toFixed(1)}%</span>
          </div>
          <div className="h-2 bg-slate-200 dark:bg-slate-800 rounded overflow-hidden">
            <div className="h-full transition-all" style={{ width: `${pctUso}%`, background: linea.color }} />
          </div>
        </div>

        {/* Acciones */}
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={onAsignar} disabled={linea.disponible <= 0 || !linea.activa} className="flex-1" data-testid={`btn-asignar-factura-${linea.id}`}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Asignar factura
          </Button>
        </div>

        {/* Asignaciones activas (top 5) */}
        {asignaciones.length > 0 && (
          <div className="border-t pt-2">
            <div className="text-[11px] font-semibold text-muted-foreground mb-1 flex items-center justify-between">
              <span>{asignaciones.length} factura{asignaciones.length !== 1 ? 's' : ''} asignada{asignaciones.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="space-y-1 max-h-52 overflow-y-auto">
              {asignaciones.map((a) => (
                <div
                  key={a.id}
                  className={`flex items-center gap-2 text-xs p-1.5 rounded ${a.vencida ? 'bg-red-50 dark:bg-red-950/20' : 'bg-slate-50 dark:bg-slate-900/30'}`}
                  data-testid={`asig-row-${a.id}`}
                >
                  <FileText className="w-3 h-3 flex-shrink-0 text-muted-foreground" />
                  <div className="flex-1 min-w-0 truncate">
                    <span className="font-semibold">{a.folio}</span>
                    <span className="text-muted-foreground"> · {a.cliente}</span>
                  </div>
                  <span className={`tabular-nums text-right ${a.vencida ? 'text-red-700 font-bold' : ''}`}>
                    {formatCLPShort(a.monto_asignado)}
                  </span>
                  <AsigFechaPagoPicker asig={a} onChange={onRefresh} />
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-emerald-600" onClick={() => marcarPagada(a)} title="Marcar pagada" data-testid={`btn-pagada-${a.id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-orange-600" onClick={() => liberar(a)} title="Liberar (reversa)" data-testid={`btn-liberar-${a.id}`}>
                    <Unlock className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      {/* AlertDialog confirmación marcar pagada (no window.confirm — puede quedar oculto bajo Dialog Radix) */}
      <AlertDialog open={!!confirmPay} onOpenChange={(o) => !o && !paying && setConfirmPay(null)}>
        <AlertDialogContent data-testid="alertdialog-pagar-factoring" className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              ¿Marcar asignación como PAGADA?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                {confirmPay?.asig && (
                  <div className="bg-slate-50 dark:bg-slate-900/40 border rounded p-2.5 space-y-1">
                    <div className="flex justify-between"><span className="text-muted-foreground">Folio:</span><span className="font-mono font-semibold">{confirmPay.asig.folio}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Cliente:</span><span className="font-medium truncate ml-2 max-w-[220px]" title={confirmPay.asig.cliente}>{confirmPay.asig.cliente}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Línea:</span><span className="font-medium">{linea.nombre}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Monto asignado:</span><span className="font-bold tabular-nums text-emerald-700">{formatCLP(confirmPay.asig.monto_asignado)}</span></div>
                  </div>
                )}
                <ul className="list-disc pl-5 text-[13px] text-muted-foreground space-y-0.5">
                  <li>Libera cupo de la línea <strong>{linea.nombre}</strong>.</li>
                  <li>Registra pago en <strong>Alegra</strong> (Banco de Chile 1) por el monto asignado.</li>
                  <li>Si es pago parcial, Alegra mantiene saldo pendiente en la factura.</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={paying} data-testid="btn-cancel-pagar-factoring">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); doMarcarPagada(); }}
              disabled={paying}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="btn-confirm-pagar-factoring"
            >
              {paying ? 'Procesando…' : 'Sí, marcar pagada'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}


/* ═══════════════════════════════════════════════════════════════════════
   Dialog: Asignar factura CxC a una línea
   ═══════════════════════════════════════════════════════════════════════ */
function AsignarFacturaDialog({ linea, onClose, onDone }) {
  const [loading, setLoading] = useState(true);
  const [cxcs, setCxcs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [monto, setMonto] = useState('');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await flujoCajaApi.cxcDisponiblesFactoring();
        setCxcs((res.data.items || []).filter((c) => c.disponible_para_factorizar > 0));
      } catch {
        toast({ title: 'Error al cargar CxC', variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return cxcs;
    const q = search.toLowerCase();
    return cxcs.filter((c) => c.folio.toLowerCase().includes(q) || c.cliente.toLowerCase().includes(q));
  }, [cxcs, search]);

  const select = (c) => {
    setSelected(c);
    // Default: asignar el mínimo entre disponible de la CxC y disponible de la línea
    const maxAllow = Math.min(c.disponible_para_factorizar, linea.disponible);
    setMonto(Math.floor(maxAllow).toString());
  };

  const asignar = async () => {
    if (!selected) return toast({ title: 'Seleccione una factura', variant: 'destructive' });
    const m = Number(monto);
    if (!m || m <= 0) return toast({ title: 'Monto inválido', variant: 'destructive' });
    if (m > selected.disponible_para_factorizar + 0.01) {
      return toast({ title: `Excede disponible de la factura (${formatCLPShort(selected.disponible_para_factorizar)})`, variant: 'destructive' });
    }
    if (m > linea.disponible + 0.01) {
      return toast({ title: `Excede cupo de la línea (${formatCLPShort(linea.disponible)})`, variant: 'destructive' });
    }
    setSaving(true);
    try {
      await flujoCajaApi.createFactoringAsignacion({
        linea_id: linea.id,
        cxc_id: selected.id,
        monto_asignado: m,
      });
      toast({ title: `Asignada ${formatCLPShort(m)} a ${linea.nombre}` });
      onDone();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="dialog-asignar">
        <DialogHeader>
          <DialogTitle>Asignar factura a {linea.nombre}</DialogTitle>
          <DialogDescription>
            Disponible en línea: <strong className="text-emerald-700">{formatCLP(linea.disponible)}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-2">
          <Search className="w-4 h-4 text-muted-foreground" />
          <Input placeholder="Buscar folio o cliente…" value={search} onChange={(e) => setSearch(e.target.value)} className="flex-1" data-testid="input-search-cxc" />
        </div>

        <div className="flex-1 overflow-y-auto border rounded">
          {loading ? (
            <div className="p-4"><Skeleton className="h-40" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">Sin facturas disponibles</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-slate-100 dark:bg-slate-900 sticky top-0">
                <tr>
                  <th className="px-2 py-1 text-left">Folio</th>
                  <th className="px-2 py-1 text-left">Cliente</th>
                  <th className="px-2 py-1 text-right">Monto total</th>
                  <th className="px-2 py-1 text-right">Ya asignado</th>
                  <th className="px-2 py-1 text-right">Disponible</th>
                  <th className="px-2 py-1 text-center">Vence</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => select(c)}
                    className={`border-t cursor-pointer hover:bg-blue-50 ${selected?.id === c.id ? 'bg-blue-100 dark:bg-blue-900/30' : ''}`}
                    data-testid={`cxc-row-${c.id}`}
                  >
                    <td className="px-2 py-1 font-mono">{c.folio}</td>
                    <td className="px-2 py-1 truncate max-w-xs">{c.cliente}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{formatCLPShort(c.monto)}</td>
                    <td className="px-2 py-1 text-right tabular-nums text-muted-foreground">
                      {c.monto_asignado_factoring > 0 ? formatCLPShort(c.monto_asignado_factoring) : '—'}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums font-bold text-emerald-700">{formatCLPShort(c.disponible_para_factorizar)}</td>
                    <td className={`px-2 py-1 text-center ${c.fecha_venc < todayISO() ? 'text-red-700 font-semibold' : ''}`}>{c.fecha_venc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {selected && (
          <div className="border-t pt-3 mt-2 space-y-2">
            <div className="text-sm">
              <strong>{selected.folio}</strong> · {selected.cliente} · Disp. {formatCLP(selected.disponible_para_factorizar)}
            </div>
            <div className="flex items-center gap-2">
              <Label className="flex-shrink-0">Monto a asignar:</Label>
              <Input type="number" value={monto} onChange={(e) => setMonto(e.target.value)} className="flex-1" data-testid="input-monto-asignar" />
              <Button
                size="sm"
                variant="outline"
                onClick={() => setMonto(Math.floor(Math.min(selected.disponible_para_factorizar, linea.disponible)).toString())}
              >
                Máx
              </Button>
            </div>
          </div>
        )}

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={asignar} disabled={!selected || saving} data-testid="btn-confirmar-asignar">
            {saving ? 'Asignando…' : 'Asignar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ═══════════════════════════════════════════════════════════════════════
   Dialog: Vencidas (por línea o global)
   ═══════════════════════════════════════════════════════════════════════ */
function VencidasDialog({ scope, onClose }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const scopeIsLinea = typeof scope === 'object';

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = { solo_vencidas: true, estado: 'asignada' };
        if (scopeIsLinea) params.linea_id = scope.id;
        const res = await flujoCajaApi.listFactoringAsignaciones(params);
        setItems(res.data.items || []);
      } catch {
        toast({ title: 'Error', variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    })();
  }, [scope, scopeIsLinea]);

  const total = items.reduce((a, x) => a + x.monto_asignado, 0);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="dialog-vencidas">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" />
            Facturas Vencidas {scopeIsLinea ? `· ${scope.nombre}` : '(todas las líneas)'}
          </DialogTitle>
          <DialogDescription>
            {items.length} factura{items.length !== 1 ? 's' : ''} asignada{items.length !== 1 ? 's' : ''} con fecha vencida · Total: <strong className="text-red-700">{formatCLP(total)}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto border rounded">
          {loading ? (
            <div className="p-4"><Skeleton className="h-40" /></div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">Sin facturas vencidas 🎉</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-red-100 sticky top-0">
                <tr>
                  <th className="px-2 py-1 text-left">Folio</th>
                  <th className="px-2 py-1 text-left">Cliente</th>
                  <th className="px-2 py-1 text-left">Línea</th>
                  <th className="px-2 py-1 text-right">Monto asignado</th>
                  <th className="px-2 py-1 text-center">F. Alegra</th>
                  <th className="px-2 py-1 text-center">F. Pago Flujo</th>
                  <th className="px-2 py-1 text-center">Días atraso</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => {
                  const efectiva = a.fecha_pago_modificada || a.fecha_venc;
                  const diffDays = efectiva ? Math.floor((new Date(todayISO()) - new Date(efectiva)) / 86400000) : 0;
                  return (
                    <tr key={a.id} className="border-t hover:bg-red-50" data-testid={`venc-row-${a.id}`}>
                      <td className="px-2 py-1 font-mono font-semibold">{a.folio}</td>
                      <td className="px-2 py-1 truncate max-w-xs">{a.cliente}</td>
                      <td className="px-2 py-1">{a.linea_nombre}</td>
                      <td className="px-2 py-1 text-right tabular-nums font-bold text-red-700">{formatCLPShort(a.monto_asignado)}</td>
                      <td className="px-2 py-1 text-center text-muted-foreground tabular-nums">{a.fecha_venc}</td>
                      <td className="px-2 py-1 text-center">
                        <AsigFechaPagoPicker asig={a} onChange={() => { /* el modal recarga al reabrir */ }} />
                      </td>
                      <td className="px-2 py-1 text-center">
                        <Badge variant={diffDays > 0 ? 'destructive' : 'outline'} className="text-[10px]">
                          {diffDays > 0 ? `+${diffDays}d` : `${diffDays}d`}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <DialogFooter>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



/* ═══════════════════════════════════════════════════════════════════════
   Sección: CxC disponibles para factorizar
   UX inteligente: dropdown por fila para asignar rápido a una línea.
   Columna "Líneas asignadas" con badges clickeables (liberar).
   ═══════════════════════════════════════════════════════════════════════ */
function CxcDisponiblesSection({ loading, items, lineas, onAsignar, onRefresh }) {
  const [search, setSearch] = useState('');
  const [filtro, setFiltro] = useState('todas'); // 'todas' | 'libres' | 'parciales' | 'asignadas'
  const [filtroLinea, setFiltroLinea] = useState('todas'); // 'todas' | linea_id | 'sin-asignar'
  const [soloVencidas, setSoloVencidas] = useState(false);
  const [sortBy, setSortBy] = useState('fecha_venc'); // campo ordenamiento
  const [sortDir, setSortDir] = useState('asc'); // asc | desc
  const [busy, setBusy] = useState(null); // cxc_id en proceso

  const today = todayISO();

  const filtered = useMemo(() => {
    let list = items;
    if (filtro === 'libres') list = list.filter((c) => (c.asignaciones?.length || 0) === 0);
    else if (filtro === 'parciales') list = list.filter((c) => (c.asignaciones?.length || 0) > 0 && c.disponible_para_factorizar > 0);
    else if (filtro === 'asignadas') list = list.filter((c) => (c.asignaciones?.length || 0) > 0 && c.disponible_para_factorizar <= 0.01);
    if (filtroLinea !== 'todas') {
      if (filtroLinea === 'sin-asignar') {
        list = list.filter((c) => (c.asignaciones?.length || 0) === 0);
      } else {
        list = list.filter((c) => (c.asignaciones || []).some((a) => a.linea_id === filtroLinea));
      }
    }
    if (soloVencidas) list = list.filter((c) => c.fecha_venc < today);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((c) => c.folio.toLowerCase().includes(q) || c.cliente.toLowerCase().includes(q));
    }
    // Ordenamiento
    const dir = sortDir === 'asc' ? 1 : -1;
    list = [...list].sort((a, b) => {
      let va, vb;
      switch (sortBy) {
        case 'folio': va = a.folio; vb = b.folio; break;
        case 'cliente': va = a.cliente; vb = b.cliente; break;
        case 'monto': va = a.monto; vb = b.monto; break;
        case 'asignado': va = a.monto_asignado_factoring; vb = b.monto_asignado_factoring; break;
        case 'disponible': va = a.disponible_para_factorizar; vb = b.disponible_para_factorizar; break;
        case 'fecha_venc': va = a.fecha_venc || ''; vb = b.fecha_venc || ''; break;
        default: va = a.fecha_venc || ''; vb = b.fecha_venc || '';
      }
      if (typeof va === 'string') return va.localeCompare(vb) * dir;
      return (va - vb) * dir;
    });
    return list;
  }, [items, search, filtro, filtroLinea, soloVencidas, sortBy, sortDir, today]);

  const toggleSort = (campo) => {
    if (sortBy === campo) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(campo); setSortDir('asc'); }
  };

  const sortIcon = (campo) => {
    if (sortBy !== campo) return <span className="text-slate-300 ml-0.5">↕</span>;
    return <span className="text-blue-600 ml-0.5 font-bold">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const total = filtered.reduce((a, c) => a + c.monto, 0);
  const totalDisp = filtered.reduce((a, c) => a + Math.max(c.disponible_para_factorizar, 0), 0);

  const asignarRapidoLinea = async (cxc, linea) => {
    setBusy(cxc.id);
    try {
      await flujoCajaApi.asignacionRapida(linea.id, cxc.id);
      toast({ title: `Asignado a ${linea.nombre}` });
      await onRefresh();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  const liberarAsignacion = async (asig_id) => {
    if (!window.confirm('¿Liberar esta asignación? Devuelve el cupo sin marcar la CxC como pagada.')) return;
    setBusy('liberar-' + asig_id);
    try {
      await flujoCajaApi.liberarAsignacion(asig_id);
      toast({ title: 'Asignación liberada' });
      await onRefresh();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  const colorLinea = (linea_id) => lineas.find((l) => l.id === linea_id)?.color || '#64748b';

  return (
    <Card data-testid="cxc-disponibles-section" className="mt-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Cuentas por Cobrar (control factoring)
          <Badge variant="outline" className="ml-1 font-mono">{filtered.length}</Badge>
          <span className="ml-auto text-xs text-muted-foreground">
            Total <span className="font-semibold text-slate-900 dark:text-slate-100 tabular-nums">{formatCLP(total)}</span>
            {' · '}
            Disp. factorizar: <span className="font-semibold text-emerald-700 tabular-nums">{formatCLP(totalDisp)}</span>
          </span>
        </CardTitle>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <div className="flex items-center gap-1 flex-1 min-w-[200px]">
            <Search className="w-3.5 h-3.5 text-muted-foreground" />
            <Input placeholder="Buscar folio o cliente…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-8" data-testid="input-search-disponibles" />
          </div>
          <div className="flex items-center gap-1 text-xs flex-wrap">
            {[
              { k: 'todas', label: 'Todas' },
              { k: 'libres', label: 'Sin asignar' },
              { k: 'parciales', label: 'Parciales' },
              { k: 'asignadas', label: '100% asignadas' },
            ].map((opt) => (
              <Button
                key={opt.k}
                size="sm"
                variant={filtro === opt.k ? 'default' : 'outline'}
                className="h-7 text-[11px]"
                onClick={() => setFiltro(opt.k)}
                data-testid={`filtro-${opt.k}`}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <select
            value={filtroLinea}
            onChange={(e) => setFiltroLinea(e.target.value)}
            className="h-8 text-xs border rounded px-2 bg-white dark:bg-slate-900"
            data-testid="select-filtro-linea"
          >
            <option value="todas">Todas las líneas</option>
            <option value="sin-asignar">Sin asignar</option>
            {lineas.map((l) => (
              <option key={l.id} value={l.id}>Línea: {l.nombre}</option>
            ))}
          </select>
          <label className="flex items-center gap-1 text-xs cursor-pointer select-none">
            <input
              type="checkbox"
              checked={soloVencidas}
              onChange={(e) => setSoloVencidas(e.target.checked)}
              data-testid="check-solo-vencidas"
            />
            <span className={soloVencidas ? 'text-red-700 font-semibold' : ''}>Solo vencidas</span>
          </label>
          <span className="text-[10px] text-muted-foreground ml-auto">
            Orden: <strong>{sortBy}</strong> {sortDir === 'asc' ? '↑' : '↓'} · click en header para cambiar
          </span>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-4"><Skeleton className="h-32" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground text-sm">Sin facturas</div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 dark:bg-slate-900 sticky top-0 z-10">
                <tr>
                  <th className="px-2 py-1.5 text-left w-20 cursor-pointer select-none hover:bg-slate-200" onClick={() => toggleSort('folio')} data-testid="sort-folio">Folio{sortIcon('folio')}</th>
                  <th className="px-2 py-1.5 text-left cursor-pointer select-none hover:bg-slate-200" onClick={() => toggleSort('cliente')} data-testid="sort-cliente">Cliente{sortIcon('cliente')}</th>
                  <th className="px-2 py-1.5 text-right cursor-pointer select-none hover:bg-slate-200" onClick={() => toggleSort('monto')} data-testid="sort-monto">Monto{sortIcon('monto')}</th>
                  <th className="px-2 py-1.5 text-left min-w-[180px]">Líneas asignadas</th>
                  <th className="px-2 py-1.5 text-right cursor-pointer select-none hover:bg-slate-200" onClick={() => toggleSort('disponible')} data-testid="sort-disponible">Disponible{sortIcon('disponible')}</th>
                  <th className="px-2 py-1.5 text-center cursor-pointer select-none hover:bg-slate-200" onClick={() => toggleSort('fecha_venc')} data-testid="sort-fecha">Vence{sortIcon('fecha_venc')}</th>
                  <th className="px-2 py-1.5 text-center w-64">Asignar a…</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const vencida = c.fecha_venc < todayISO();
                  const asigs = c.asignaciones || [];
                  const disponibleCxC = Math.max(c.disponible_para_factorizar, 0);
                  return (
                    <tr key={c.id} className="border-t hover:bg-blue-50/50 dark:hover:bg-blue-950/20" data-testid={`disp-row-${c.id}`}>
                      <td className="px-2 py-1 font-mono font-semibold">{c.folio}</td>
                      <td className="px-2 py-1 truncate max-w-[200px]" title={c.cliente}>{c.cliente}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{formatCLPShort(c.monto)}</td>
                      <td className="px-2 py-1">
                        {asigs.length === 0 ? (
                          <span className="text-muted-foreground italic">— sin asignar —</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {asigs.map((a) => (
                              <Badge
                                key={a.asig_id}
                                className="text-[10px] cursor-pointer hover:opacity-80"
                                style={{ background: colorLinea(a.linea_id), color: '#fff' }}
                                title="Clic para liberar"
                                onClick={() => liberarAsignacion(a.asig_id)}
                                data-testid={`badge-asig-${a.asig_id}`}
                              >
                                {a.linea_nombre}: {formatCLPShort(a.monto)} ×
                              </Badge>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className={`px-2 py-1 text-right tabular-nums font-bold ${disponibleCxC > 0 ? 'text-emerald-700' : 'text-muted-foreground'}`}>
                        {disponibleCxC > 0 ? formatCLPShort(disponibleCxC) : '✓'}
                      </td>
                      <td className={`px-2 py-1 text-center text-[11px] ${vencida ? 'text-red-700 font-semibold' : ''}`}>{c.fecha_venc}</td>
                      <td className="px-2 py-1">
                        <div className="flex items-center gap-1 justify-center">
                          {lineas.map((l) => {
                            const yaAsignado = asigs.some((a) => a.linea_id === l.id);
                            const disabled = busy === c.id || disponibleCxC <= 0 || l.disponible <= 0;
                            return (
                              <Button
                                key={l.id}
                                size="sm"
                                variant={yaAsignado ? 'default' : 'outline'}
                                className="h-6 text-[10px] px-1.5"
                                disabled={disabled}
                                onClick={() => asignarRapidoLinea(c, l)}
                                style={yaAsignado ? { background: l.color } : { borderColor: l.color, color: l.color }}
                                title={disabled ? 'CxC sin disponible o línea sin cupo' : `Asignar ${formatCLPShort(Math.min(disponibleCxC, l.disponible))} a ${l.nombre}`}
                                data-testid={`btn-rapido-${c.id}-${l.id}`}
                              >
                                {l.nombre.split(' ')[0]}
                              </Button>
                            );
                          })}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 text-[10px] px-1.5"
                            onClick={() => {
                              const cand = lineas.filter((l) => l.disponible > 0)[0];
                              if (cand) onAsignar(cand);
                              else toast({ title: 'No hay líneas con cupo', variant: 'destructive' });
                            }}
                            title="Asignar parcial (modal)"
                            data-testid={`btn-parcial-${c.id}`}
                          >
                            …
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
